import React, { Fragment, memo, useEffect, useCallback, useMemo, useRef } from "react";

import { useAxiomState } from "__AxiomComponents/AxiomHooks/AxiomHooks";
import { useWidthHeightCalculator } from "__AxiomComponents/AxiomHooks/useWidthHeightCalculator";
import AxiomLoader from "__AxiomComponents/AxiomUI/AxiomLoader/AxiomLoader.jsx";

import InsightStory from "../InsightStory/InsightStory.js";
import InsightList from "../InsightList/InsightList.js";
import InsightStatements from "../InsightStatements/InsightStatements.js";

import "./InsightApp.scss";

function InsightApp(props) {
  const { insightList, loading, storyList, changeStory, circleData, field, question } = props;

  const [state, dispatch] = useAxiomState(
    {
      selectedInsightIndex: -1,
      autoplay: true,
      minCircles: 1500,
    },
    props.appName,
    props.appID,
    "InsightApp",
    props.componentID
  );

  const { selectedInsightIndex, autoplay, minCircles } = state;

  const [contentRef, width, height] = useWidthHeightCalculator();

  const executeInsight = useCallback(
    (index) => {
      if (index >= 0 && index < insightList.length) {
        if (selectedInsightIndex === index - 1 || selectedInsightIndex === index + 1) {
          dispatch({ selectedInsightIndex: index });
        }
      }
    },
    [dispatch, selectedInsightIndex, insightList.length]
  );

  const handleAutoplayChange = useCallback((autoplay) => dispatch({ autoplay }), [dispatch]);

  // Reset insight story current step 1 on circle data change
  useEffect(() => dispatch({ selectedInsightIndex: 0 }), [dispatch, circleData]);

  // Compute circles from prop circle data
  const computedCircles = useMemo(() => circleData.map((d) => ({ key: d[field], data: d })), [
    circleData,
    field,
  ]);

  // Handle Keyboard shortcuts
  useKeyboardShortcuts(dispatch, executeInsight, insightList.length);

  // Handle autoplay of story
  useAutoPlay(autoplay, circleData, selectedInsightIndex, executeInsight, insightList.length);

  const selectedInsightObject =
    selectedInsightIndex > -1 ? insightList[selectedInsightIndex] : null;

  return (
    <Fragment>
      {loading && (
        <div>
          <AxiomLoader
            background={"rgba(255, 255, 255, 0.4)"}
            fullScreen={true}
            block=".insight-story-app"
          />
        </div>
      )}
      <div className="insight-story-app">
        <div className="insight-story-list">
          {question && <h1 className="insight-story-title">{question}</h1>}
          {storyList.length > 0 && (
            <InsightStatements onSlideChange={changeStory} statements={storyList} />
          )}
        </div>
        <div className="insight-story-details">
          <div ref={contentRef} className="insight-story-chart">
            {selectedInsightObject && width && height && computedCircles && (
              <InsightStory
                width={width - 40}
                height={height - 30}
                circleData={computedCircles}
                minCircles={minCircles}
                step={selectedInsightObject.stepInfo}
                appName={props.appName}
                appID={props.appID}
                componentID={0}
              />
            )}
          </div>
          <InsightList
            executeInsight={executeInsight}
            selectedInsightIndex={selectedInsightIndex}
            insightList={insightList}
            autoplay={autoplay}
            onAutoplayChange={handleAutoplayChange}
            appName={props.appName}
            appID={props.appID}
            componentID={props.componentID}
          />
        </div>
      </div>
    </Fragment>
  );
}

function useKeyboardShortcuts(dispatch, executeInsight, insightListLength) {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.keyCode === 32) {
        dispatch((state) => ({ autoplay: !state.autoplay }));
      } else if (e.keyCode === 37) {
        // Previous
        dispatch((state) => {
          if (state.selectedInsightIndex > 0) {
            return { selectedInsightIndex: state.selectedInsightIndex - 1 };
          } else {
            return null;
          }
        });
      } else if (e.keyCode === 39) {
        // next
        dispatch((state) => {
          if (state.selectedInsightIndex < insightListLength - 1) {
            return { selectedInsightIndex: state.selectedInsightIndex + 1 };
          } else {
            return null;
          }
        });
      }
    };
    document.addEventListener("keydown", handleKeyDown, false);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, false);
    };
  }, [dispatch, executeInsight, insightListLength]);
}

function useAutoPlay(
  autoplay,
  circleData,
  selectedInsightIndex,
  executeInsight,
  insightListLength
) {
  useEffect(() => {
    if (autoplay) {
      const timerID = setTimeout(() => {
        if (selectedInsightIndex < insightListLength - 1) {
          executeInsight(selectedInsightIndex + 1);
        }
      }, 5000);
      return () => {
        clearTimeout(timerID);
      };
    }
  }, [autoplay, circleData, selectedInsightIndex, executeInsight, insightListLength]);
}

export default memo(InsightApp);
